import { Box, Button, Slider, TextField, Typography } from "@mui/material";
import { palette, themeWidth } from "../../themes/theme";
import { useContext, useEffect, useState } from "react";
import DarkPrimaryButton from "../PrimaryButton/DarkPrimaryButton";
import { MapLayoutWidth } from "../../utils/helper";
import { MapToolboxContext } from "../../context/mapToolbox/mapToolbox.provider";
import ColorPicker from "../ColorPicker";
import { CustomDraggable } from "../Draggable";
import { useDispatch } from "react-redux";
import { updateHighwaySetting } from "../../redux/slices/mapSettingsSlice";

export const AddHighWayLayer = ({ mapRef, highwayStyle, visibility }) => {
  if (!mapRef.getLayer("highway-layer")) {
    mapRef.addLayer({
      id: "highway-layer",
      type: "line",
      source: {
        type: "vector",
        url: "mapbox://mapbox.mapbox-streets-v8",
      },
      "source-layer": "road",
      filter: ["any", ["==", "class", "motorway"], ["==", "class", "trunk"]],
      // Adjust this filter based on the highway classification you want to highlight
      layout: {
        "line-join": "round",
        "line-cap": "round",
        visibility: "none", // Initially hidden
      },
      paint: {
        "line-color": highwayStyle.highwayColor, // Change the color as needed
        "line-width": highwayStyle.highwaySize,
        "line-opacity": highwayStyle.highwayOpacity,
      },
    });
    mapRef.setLayoutProperty(
      "highway-layer",
      "visibility",
      visibility ? "visible" : "none"
    );
  }
};

const HighwaySetting = ({ mapRef, highwayStyleSettings }) => {
  const { showHighwayStyle, setShowHighwayStyle } =
    useContext(MapToolboxContext);

  const dispatch = useDispatch();

  const [mapState, setMapState] = useState({});

  const changeStyle = (key, value) => {
    let settingsKey = "";
    switch (key) {
      case "opacity":
        if (!isNaN(value)) {
          mapRef.setPaintProperty(
            "highway-layer",
            "line-opacity",
            parseFloat(value)
          );
        }
        settingsKey = "highwayOpacity";
        break;
      case "color":
        mapRef.setPaintProperty("highway-layer", "line-color", value);
        settingsKey = "highwayColor";
        break;
      case "size":
        mapRef.setPaintProperty(
          "highway-layer",
          "line-width",
          Number(value) < 0 ? 0 : Number(value)
        );
        settingsKey = "highwaySize";
        break;
      default:
        break;
    }

    setMapState((d) => ({ ...d, [key]: value }));
    dispatch(updateHighwaySetting({ name: settingsKey, value }));
  };

  const handleOk = () => {
    setShowHighwayStyle(false);
  };

  useEffect(() => {
    if (showHighwayStyle) {
      if (!mapRef.getPaintProperty("highway-layer", "line-color")) {
        AddHighWayLayer({
          mapRef,
          highwayStyle: highwayStyleSettings,
          visibility: showHighwayStyle,
        });
      }
      const color = mapRef.getPaintProperty("highway-layer", "line-color");
      const size = mapRef.getPaintProperty("highway-layer", "line-width");
      const opacity = mapRef.getPaintProperty("highway-layer", "line-opacity");
      setMapState({
        color,
        size,
        opacity,
      });
    }
  }, [showHighwayStyle]);

  return (
    <>
      {showHighwayStyle && (
        <CustomDraggable
          width={300}
          height={230}
          left={themeWidth[MapLayoutWidth] + 10}
          top={window.innerHeight - 230 - 120}
          headerTitle={"Highway Settings"}
          disableOverflow={true}
          customFooter={
            <Button
              variant="outlined"
              onClick={handleOk}
              sx={{ borderRadius: "0px", fontWeight: "bold" }}
            >
              Ok
            </Button>
          }
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              p: 1,
            }}
          >
            <Box sx={{ display: "flex", gap: 2 }}>
              <ColorPicker
                title={"Fill"}
                color={mapState.color}
                setColor={changeStyle}
                type={"color"}
                noCheckbox={true}
              />
              <TextField
                type="number"
                sx={{
                  width: "65px",
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "white",
                    color: "black",
                    borderRadius: "4px",
                    "& fieldset": {
                      borderColor: "black", // default border
                    },
                    "&:hover fieldset": {
                      borderColor: "black", // on hover
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "black", // on focus
                    },
                  },
                  "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                    {
                      display: "none",
                    },
                  "& input[type=number]": {
                    MozAppearance: "textfield",
                  },
                  "& .MuiInputLabel-root": {
                    color: "black", // label color
                  },
                }}
                label="Size"
                value={Number(mapState.size) < 0 ? 0 : Number(mapState.size)}
                onChange={(e) => changeStyle("size", e.target.value)}
                InputLabelProps={{
                  style: { color: "black" },
                }}
              />
            </Box>
            <Typography sx={{ color: palette.text.dark, fontSize: 15 }}>
              Transparency: {mapState.opacity * 100}%
            </Typography>
            <Slider
              min={0}
              step={0.1}
              max={1}
              value={mapState.opacity || 0}
              valueLabelDisplay="off"
              onChange={(e) => changeStyle("opacity", e.target.value)}
              sx={{ width: "85%", alignSelf: "center" }}
            />
          </Box>
        </CustomDraggable>
      )}
    </>
  );
};

export default HighwaySetting;
